import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useIntakeForm, IntakeProvider } from '@/contexts/IntakeContext';
import IntakeProgress from '@/components/intake/IntakeProgress';
import QuestionnaireADAM from '@/components/intake/QuestionnaireADAM';
import QuestionnaireStopBang from '@/components/intake/QuestionnaireStopBang';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowRight, 
  ArrowLeft, 
  User, 
  AlertCircle, 
  CheckCircle2, 
  Loader2,
  Sparkles,
  Shield,
  Clock,
  RefreshCw,
  Save
} from 'lucide-react';
import { DatePicker } from '@/components/ui/date-picker';
import { format, parse, isValid } from 'date-fns';
import { parseLocalDate } from '@/lib/dateUtils';
import ShippingBillingAddressStep from '@/components/intake/ShippingBillingAddressStep';
import AllergyMultiSelect from '@/components/ui/allergy-multi-select';
import MedicalHistoryMultiSelect from '@/components/ui/medical-history-multi-select';
import PaymentStep from '@/components/intake/shared/PaymentStep';
import IntakeBookingStep from '@/components/intake/shared/IntakeBookingStep';
import { CalendarButtons } from '@/components/booking/CalendarButtons';
import type { GuidedPaymentStatus } from '@/types/guidedOrder';

type BookedAppointment = {
  id: string;
  start_time: string;
  end_time: string;
  duration: number;
  telehealthLink?: string | null;
};

// Token validation landing page
const IntakeLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { validateToken, formState, updateFormState, targetedNav } = useIntakeForm();
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get the base path to preserve therapy-specific routes
  const basePath = useMemo(() => {
    // Extract base path like "/patient/intake/mens" or "/patient/intake"
    const path = location.pathname;
    const tokenIndex = path.lastIndexOf('/' + token);
    return tokenIndex > 0 ? path.substring(0, tokenIndex) : '/patient/intake';
  }, [location.pathname, token]);

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setError('No intake token provided');
        setIsValidating(false);
        return;
      }

      const result = await validateToken(token);
      if (!result.success) {
        setError(result.error || 'Invalid or expired intake link');
        setIsValidating(false);
        return;
      }
      
      // Redirect to specialized forms based on therapy category
      const therapyCategory = result.data?.therapyCategory;
      if (therapyCategory === 'trt') {
        navigate(`/patient/intake/mens/${token}`, { replace: true });
        return;
      } else if (therapyCategory === 'hrt') {
        navigate(`/patient/intake/womens/${token}`, { replace: true });
        return;
      }
      
      setIsValidating(false);
    };

    validate();
  }, [token, validateToken, navigate]);

  if (isValidating) {
    return (
      <div className="min-h-screen bg-[#0A0F1A] flex items-center justify-center intake-dark-form">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#4A8A9A] mx-auto mb-4" />
          <p className="text-white/60">Validating your intake link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0F1A] flex items-center justify-center p-4 intake-dark-form">
        <Card className="max-w-md w-full bg-white/5 border-white/10">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-red-400 mx-auto" />
              <h1 className="text-2xl font-bold text-white font-montserrat">Invalid Intake Link</h1>
              <p className="text-white/60">
                {error}
              </p>
              <p className="text-sm text-white/40">
                Please contact your healthcare provider to request a new intake link.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate expiry warning
  const expiresDate = new Date(formState.expires_at);
  const daysUntilExpiry = Math.ceil((expiresDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  const expiresSoon = daysUntilExpiry <= 7;

  const handleStart = () => {
    const firstStep = targetedNav.activeSteps[0] || 1;
    updateFormState({ currentStep: firstStep });
    navigate(`${basePath}/${token}/form?step=${firstStep}`);
  };

  const handleResume = () => {
    // Resume from the saved step
    navigate(`/patient/intake/${token}/form?step=${formState.currentStep}`);
  };

  const handleStartFresh = () => {
    // Clear draft and start fresh
    updateFormState({ 
      currentStep: 1,
      hasDraft: false,
      basic_info: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        biological_sex: '',
        city: '',
        state: '',
        zip_code: '',
      },
      medical_history: {
        conditions: [],
        medications: [],
        allergies: [],
        surgeries: [],
        family_history: [],
        other: '',
      },
      questionnaires: {
        adam: {},
        stopBang: {},
        symptoms: {},
      },
      lifestyle: {
        exercise_frequency: '',
        diet_quality: '',
        sleep_hours: 7,
        alcohol_frequency: '',
        smoking_status: '',
        stress_level: 5,
        goals: [],
      },
      shipping_billing: {
        shippingAddressLine1: '',
        shippingAddressLine2: '',
        shippingCity: '',
        shippingState: '',
        shippingPostalCode: '',
        billingAddressLine1: '',
        billingAddressLine2: '',
        billingCity: '',
        billingState: '',
        billingPostalCode: '',
        billingSameAsShipping: true,
      },
    });
    navigate(`/patient/intake/${token}/form`);
  };

  // Format last saved time
  const formatLastSaved = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-[#0A0F1A] intake-dark-form">
      <div className="container mx-auto py-12 px-4 max-w-4xl">
        {/* Draft Resume Banner */}
        {formState.hasDraft && (
          <Card className="mb-6 bg-[#4A8A9A]/10 border-[#4A8A9A]/30">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#4A8A9A]/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <RefreshCw className="h-6 w-6 text-[#4A8A9A]" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold mb-1 text-white font-montserrat">
                    Continue Where You Left Off
                  </h2>
                  <p className="text-sm text-white/60 mb-3">
                    You have a saved progress from {formatLastSaved(formState.lastSavedAt)}.
                    You were on step {formState.currentStep} of 6.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      onClick={handleResume}
                      className="bg-[#4A8A9A] hover:bg-[#4A8A9A]/90 text-white"
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Resume Progress
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={handleStartFresh}
                      className="bg-transparent border-white/30 !text-white hover:bg-white/10 hover:!text-white"
                    >
                      Start Fresh
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Provider Context Banner */}
        <Card className="mb-8 bg-white/5 border-white/10">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#4A8A9A]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="h-6 w-6 text-[#4A8A9A]" />
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wide text-white/50 font-medium mb-1">
                  {formState.clinic_name || 'Nimbus Providers'}
                </p>
                <p className="text-sm text-[#4A8A9A] font-medium mb-1">
                  {formState.clinic_name ? 'Partnered with Nimbus Providers' : 'Powered by Nimbus Providers'}
                </p>
                <h2 className="text-xl font-semibold mb-2 text-white font-montserrat">
                  Requested by {formState.provider_name ? `Dr. ${formState.provider_name}` : ''}
                </h2>
                <p className="text-sm text-white/60 mb-2">
                  Your healthcare provider has requested that you complete this health intake questionnaire.
                </p>
                {expiresSoon && (
                  <p className="text-sm text-amber-400 font-medium">
                    ⚠️ This link expires in {daysUntilExpiry} {daysUntilExpiry === 1 ? 'day' : 'days'}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-[#4A8A9A] font-montserrat">
            {targetedNav.isTargeted ? 'Additional Information Needed' : 'Complete Your Health Intake'}
          </h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            {targetedNav.isTargeted
              ? `Your provider needs a few more details to complete your assessment. This should only take about ${targetedNav.activeStepConfigs.reduce((t, s) => t + s.estimatedMinutes, 0)} minutes.`
              : 'Please complete the following questionnaire so your provider can review your case and provide personalized recommendations.'}
          </p>
        </div>

        {/* How It Works */}
        <Card className="mb-8 bg-white/5 border-white/10">
          <CardContent className="pt-6">
            <h2 className="text-2xl font-semibold mb-6 text-center text-white font-montserrat">What to Expect</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center space-y-3">
                <div className="w-12 h-12 bg-[#4A8A9A]/20 text-[#4A8A9A] rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                  1
                </div>
                <h3 className="font-semibold text-white">{targetedNav.isTargeted ? 'Quick Questions' : 'Complete Questionnaire'}</h3>
                <p className="text-sm text-white/60">
                  {targetedNav.isTargeted
                    ? `Answer ${targetedNav.totalActiveSteps - 1} short section${targetedNav.totalActiveSteps - 1 !== 1 ? 's' : ''} your provider needs`
                    : 'Answer questions about your health history, symptoms, and goals (10-15 minutes)'}
                </p>
              </div>
              <div className="text-center space-y-3">
                <div className="w-12 h-12 bg-[#4A8A9A]/20 text-[#4A8A9A] rounded-full flex items-center justify-center mx-auto">
                  <Sparkles className="h-6 w-6" />
                </div>
                <h3 className="font-semibold text-white">AI-Powered Analysis</h3>
                <p className="text-sm text-white/60">
                  Your data is analyzed to generate protocol recommendations
                </p>
              </div>
              <div className="text-center space-y-3">
                <div className="w-12 h-12 bg-[#4A8A9A]/20 text-[#4A8A9A] rounded-full flex items-center justify-center mx-auto">
                  <Shield className="h-6 w-6" />
                </div>
                <h3 className="font-semibold text-white">Provider Review</h3>
                <p className="text-sm text-white/60">
                  {formState.provider_name} will review your case and provide personalized recommendations
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA Button */}
        <div className="text-center space-y-4">
          <Button 
            size="lg" 
            onClick={handleStart}
            className="bg-[#4A8A9A] hover:bg-[#4A8A9A]/90 text-white font-semibold text-lg px-8 py-6"
          >
            {targetedNav.isTargeted ? 'Start Quick Intake' : 'Start Your Intake Questionnaire'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="text-sm text-white/60 flex items-center justify-center gap-2">
            <Shield className="h-4 w-4" />
            Your responses are secure and HIPAA-compliant
          </p>
        </div>
      </div>
    </div>
  );
};

// Form field with inline validation component
interface FormFieldProps {
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}

const FormField: React.FC<FormFieldProps> = ({ label, required, error, hint, children }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Label className={`text-white ${error ? 'text-red-400' : ''}`}>
        {label} {required && <span className="text-red-400">*</span>}
      </Label>
      {hint && !error && (
        <span className="text-xs text-white/40">{hint}</span>
      )}
    </div>
    {children}
    {error && (
      <div className="flex items-center gap-1.5 text-sm text-red-400">
        <AlertCircle className="h-3.5 w-3.5" />
        <span>{error}</span>
      </div>
    )}
  </div>
);

// Step 1: Basic Information
const Step1BasicInfo: React.FC = () => {
  const { formState, updateFormState, targetedNav } = useIntakeForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useParams<{ token: string }>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (formState.smsConsent === undefined) {
      updateFormState({ smsConsent: true });
    }
  }, [formState.smsConsent, updateFormState]);

  // Get the base path to preserve therapy-specific routes
  const basePath = useMemo(() => {
    const path = location.pathname;
    const formIndex = path.indexOf('/form');
    if (formIndex > 0) {
      const beforeForm = path.substring(0, formIndex);
      const tokenIndex = beforeForm.lastIndexOf('/' + token);
      return tokenIndex > 0 ? beforeForm.substring(0, tokenIndex) : '/patient/intake';
    }
    return '/patient/intake';
  }, [location.pathname, token]);

  const handleChange = (field: string, value: string) => {
    updateFormState({
      basic_info: { ...formState.basic_info, [field]: value }
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validate a single field on blur
  const validateFieldOnBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const info = formState.basic_info;
    let error = '';

    switch (field) {
      case 'first_name':
        if (!info.first_name?.trim()) {
          error = 'First name is required';
        } else if (info.first_name.trim().length < 2) {
          error = 'First name must be at least 2 characters';
        }
        break;
      case 'last_name':
        if (!info.last_name?.trim()) {
          error = 'Last name is required';
        } else if (info.last_name.trim().length < 2) {
          error = 'Last name must be at least 2 characters';
        }
        break;
      case 'email':
        if (!info.email?.trim()) {
          error = 'Email address is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(info.email)) {
          error = 'Please enter a valid email (e.g., name@example.com)';
        }
        break;
      case 'phone':
        if (info.phone) {
          const cleanPhone = info.phone.replace(/\D/g, '');
          if (cleanPhone.length < 10) {
            error = 'Please enter a valid phone number';
          }
        }
        break;
      case 'date_of_birth':
        if (!info.date_of_birth) {
          error = 'Date of birth is required';
        } else {
          const birthDate = parseLocalDate(info.date_of_birth);
          const today = new Date();
          if (birthDate > today) {
            error = 'Date of birth cannot be in the future';
          } else {
            const age = today.getFullYear() - birthDate.getFullYear();
            if (age < 18) {
              error = 'You must be at least 18 years old';
            } else if (age > 120) {
              error = 'Please enter a valid date of birth';
            }
          }
        }
        break;
      case 'biological_sex':
        if (!info.biological_sex) {
          error = 'Please select your biological sex';
        }
        break;
    }

    setErrors(prev => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    const info = formState.basic_info;

    // First name
    if (!info.first_name?.trim()) {
      newErrors.first_name = 'First name is required';
    } else if (info.first_name.trim().length < 2) {
      newErrors.first_name = 'First name must be at least 2 characters';
    }

    // Last name
    if (!info.last_name?.trim()) {
      newErrors.last_name = 'Last name is required';
    } else if (info.last_name.trim().length < 2) {
      newErrors.last_name = 'Last name must be at least 2 characters';
    }

    // Email
    if (!info.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(info.email)) {
      newErrors.email = 'Please enter a valid email (e.g., name@example.com)';
    }

    // Phone (optional)
    if (info.phone) {
      const cleanPhone = info.phone.replace(/\D/g, '');
      if (cleanPhone.length < 10) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }

    // Date of birth
    if (!info.date_of_birth) {
      newErrors.date_of_birth = 'Date of birth is required';
    } else {
      const birthDate = parseLocalDate(info.date_of_birth);
      const today = new Date();
      if (birthDate > today) {
        newErrors.date_of_birth = 'Date of birth cannot be in the future';
      } else {
        const age = today.getFullYear() - birthDate.getFullYear();
        if (age < 18) {
          newErrors.date_of_birth = 'You must be at least 18 years old';
        }
      }
    }

    // Biological sex
    if (!info.biological_sex) {
      newErrors.biological_sex = 'Please select your biological sex';
    }

    setErrors(newErrors);
    // Mark all fields as touched
    setTouched({
      first_name: true,
      last_name: true,
      email: true,
      phone: true,
      date_of_birth: true,
      biological_sex: true,
    });
    
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      const next = targetedNav.nextStep(1) || 2;
      updateFormState({ currentStep: next });
      navigate(`${basePath}/${token}/form?step=${next}`);
    } else {
      // Scroll to first error
      const firstErrorField = document.querySelector('[data-error="true"]');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Calculate form completion for this step
  const requiredFields = ['first_name', 'last_name', 'email', 'date_of_birth', 'biological_sex'];
  const completedFields = requiredFields.filter(field => {
    const value = formState.basic_info[field as keyof typeof formState.basic_info];
    return value && String(value).trim() !== '';
  });
  const completionPercent = Math.round((completedFields.length / requiredFields.length) * 100);

  return (
    <Card className="bg-white/5 border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white font-montserrat">Basic Information</CardTitle>
            <CardDescription className="text-white/60">
              Let's start with some basic details about you
            </CardDescription>
          </div>
          <Badge variant="outline" className={`border-white/20 text-white/60 ${completionPercent === 100 ? 'bg-[#4A8A9A]/20 text-[#4A8A9A] border-[#4A8A9A]/30' : ''}`}>
            {completionPercent}% complete
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Name Fields */}
        <div className="grid md:grid-cols-2 gap-4">
          <FormField 
            label="First Name" 
            required 
            error={touched.first_name ? errors.first_name : undefined}
            hint="Your legal first name"
          >
            <Input
              value={formState.basic_info.first_name}
              onChange={(e) => handleChange('first_name', e.target.value)}
              onBlur={() => validateFieldOnBlur('first_name')}
              placeholder="John"
              data-error={!!errors.first_name}
              className={`bg-white/5 border-white/20 text-white placeholder:text-white/30 ${errors.first_name && touched.first_name ? 'border-red-400 ring-1 ring-red-400/30' : ''}`}
            />
          </FormField>
          <FormField 
            label="Last Name" 
            required 
            error={touched.last_name ? errors.last_name : undefined}
            hint="Your legal last name"
          >
            <Input
              value={formState.basic_info.last_name}
              onChange={(e) => handleChange('last_name', e.target.value)}
              onBlur={() => validateFieldOnBlur('last_name')}
              placeholder="Doe"
              data-error={!!errors.last_name}
              className={`bg-white/5 border-white/20 text-white placeholder:text-white/30 ${errors.last_name && touched.last_name ? 'border-red-400 ring-1 ring-red-400/30' : ''}`}
            />
          </FormField>
        </div>

        {/* Contact Fields */}
        <div className="grid md:grid-cols-2 gap-4">
          <FormField 
            label="Email Address" 
            required 
            error={touched.email ? errors.email : undefined}
            hint="e.g., name@example.com"
          >
            <Input
              type="email"
              value={formState.basic_info.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={() => validateFieldOnBlur('email')}
              placeholder="john.doe@example.com"
              data-error={!!errors.email}
              className={`bg-white/5 border-white/20 text-white placeholder:text-white/30 ${errors.email && touched.email ? 'border-red-400 ring-1 ring-red-400/30' : ''}`}
            />
          </FormField>
          <FormField 
            label="Phone Number" 
            error={touched.phone ? errors.phone : undefined}
            hint="For appointment reminders"
          >
            <Input
              type="tel"
              value={formState.basic_info.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              onBlur={() => validateFieldOnBlur('phone')}
              placeholder="512-608-9355"
              className={`bg-white/5 border-white/20 text-white placeholder:text-white/30 ${errors.phone && touched.phone ? 'border-red-400 ring-1 ring-red-400/30' : ''}`}
            />
          </FormField>
        </div>

        {/* SMS Consent */}
        <div className="flex items-start space-x-3 mt-2 p-3 rounded-lg bg-white/[0.02] border border-white/10">
          <Checkbox
            id="sms-consent"
            className="mt-0.5 border-white/30 data-[state=checked]:bg-[#4A8A9A] data-[state=checked]:border-[#4A8A9A]"
            checked={formState.smsConsent ?? true}
            onCheckedChange={(checked) => {
              updateFormState({ smsConsent: checked as boolean });
            }}
          />
          <div className="grid gap-1 leading-none">
            <label htmlFor="sms-consent" className="text-sm font-medium cursor-pointer text-white/90">
              I agree to receive text messages from Nimbus Providers
            </label>
            <p className="text-xs leading-relaxed text-white/50">
              Including prescription updates, refill reminders, and order status notifications.
              Msg frequency varies. Msg &amp; data rates may apply. Reply STOP to cancel. Reply HELP for help.
            </p>
          </div>
        </div>

        {/* Date of Birth */}
        <FormField 
          label="Date of Birth" 
          required 
          error={touched.date_of_birth ? errors.date_of_birth : undefined}
          hint="Used for age-specific recommendations (MM/DD/YYYY)"
        >
          <DatePicker
            value={formState.basic_info.date_of_birth ? (() => {
              const parsedDate = parse(formState.basic_info.date_of_birth, 'yyyy-MM-dd', new Date());
              return isValid(parsedDate) ? parsedDate : undefined;
            })() : undefined}
            onValueChange={(date) => {
              if (date && isValid(date)) {
                handleChange('date_of_birth', format(date, 'yyyy-MM-dd'));
                setErrors(prev => ({ ...prev, date_of_birth: '' }));
              } else {
                handleChange('date_of_birth', '');
              }
              setTouched(prev => ({ ...prev, date_of_birth: true }));
            }}
            placeholder="MM/DD/YYYY"
            maxDate={new Date()}
            className={`bg-white/5 border-white/20 text-white ${errors.date_of_birth && touched.date_of_birth ? 'border-red-400 ring-1 ring-red-400/30' : ''}`}
          />
        </FormField>

        {/* Biological Sex */}
        <FormField 
          label="Biological Sex" 
          required 
          error={touched.biological_sex ? errors.biological_sex : undefined}
          hint="Important for hormone therapy protocols"
        >
          <div className="grid grid-cols-3 gap-3 pt-2">
            {[
              { value: 'male', label: 'Male' },
              { value: 'female', label: 'Female' },
              { value: 'unknown', label: 'Unknown' },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  handleChange('biological_sex', option.value);
                  setErrors(prev => ({ ...prev, biological_sex: '' }));
                  setTouched(prev => ({ ...prev, biological_sex: true }));
                }}
                className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-all font-medium ${
                  formState.basic_info.biological_sex === option.value
                    ? 'border-[#4A8A9A] bg-[#4A8A9A]/15 text-[#4A8A9A]'
                    : 'border-white/20 text-white/70 hover:border-white/40 hover:bg-white/5'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </FormField>

        {/* Error Summary */}
        {Object.keys(errors).some(key => errors[key] && touched[key as keyof typeof touched]) && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <div className="flex items-center gap-2 text-red-400 text-sm font-medium mb-2">
              <AlertCircle className="h-4 w-4" />
              Please fix the following errors:
            </div>
            <ul className="text-sm text-red-400/80 list-disc list-inside space-y-1">
              {Object.entries(errors).map(([field, error]) => 
                error && touched[field as keyof typeof touched] ? (
                  <li key={field}>{error}</li>
                ) : null
              )}
            </ul>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={() => navigate(`${basePath}/${token}`)}
            className="bg-[#1E2028] border-[#3B4252] text-white hover:bg-[#2A2F3A] font-medium"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button
            onClick={handleNext}
            className="bg-[#4A8A9A] hover:bg-[#4A8A9A]/90 text-white font-medium"
          >
            {(() => {
              const next = targetedNav.nextStep(1);
              if (next === 2) return 'Next: Health History';
              if (next === 3) return 'Next: Questionnaires';
              if (next === 4) return 'Next: Lifestyle';
              if (next === 5) return 'Next: Address';
              if (next === 6) return 'Next: Review';
              return 'Next';
            })()}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Step 2: Medical History
const Step2MedicalHistory: React.FC = () => {
  const { formState, updateFormState, targetedNav } = useIntakeForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useParams<{ token: string }>();
  const [newMedication, setNewMedication] = useState('');

  const basePath = useMemo(() => {
    const path = location.pathname;
    const formIndex = path.indexOf('/form');
    if (formIndex > 0) {
      const beforeForm = path.substring(0, formIndex);
      const tokenIndex = beforeForm.lastIndexOf('/' + token);
      return tokenIndex > 0 ? beforeForm.substring(0, tokenIndex) : '/patient/intake';
    }
    return '/patient/intake';
  }, [location.pathname, token]);

  const addItem = (field: 'conditions' | 'medications' | 'allergies', value: string) => {
    if (value.trim()) {
      updateFormState({
        medical_history: {
          ...formState.medical_history,
          [field]: [...formState.medical_history[field], value.trim()]
        }
      });
    }
  };

  const removeItem = (field: 'conditions' | 'medications' | 'allergies', index: number) => {
    updateFormState({
      medical_history: {
        ...formState.medical_history,
        [field]: formState.medical_history[field].filter((_, i) => i !== index)
      }
    });
  };

  const mf = formState.missing_fields;
  const isTargeted = targetedNav.isTargeted;

  // In targeted mode, show only the medical history subsections that are needed
  const showConditions = !isTargeted || mf.some(f => ['medical_history', 'conditions', 'past_surgeries'].includes(f));
  const showMedications = !isTargeted || mf.some(f => ['medical_history', 'current_medications', 'medications'].includes(f));
  const showAllergies = !isTargeted || mf.some(f => ['medical_history', 'allergies'].includes(f));

  return (
    <Card className="bg-white border shadow-sm">
      <CardHeader>
        <CardTitle className="text-foreground font-montserrat">Medical History</CardTitle>
        <CardDescription className="text-muted-foreground">
          {isTargeted
            ? 'Your provider needs additional details about your medical background'
            : 'Help us understand your health background'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Conditions */}
        {showConditions && <div className="space-y-3">
          <MedicalHistoryMultiSelect
            dark
            label="Current Medical Conditions"
            placeholder="Select medical conditions..."
            value={{
              conditions: formState.medical_history.conditions,
              other: formState.medical_history.other || '',
            }}
            onChange={(next) => updateFormState({
              medical_history: {
                ...formState.medical_history,
                conditions: next.conditions,
                other: next.other,
              },
            })}
          />
        </div>}

        {/* Current Medications */}
        {showMedications && <div className="space-y-3">
          <Label className="text-foreground">Current Medications</Label>
          <div className="flex gap-2">
            <Input
              value={newMedication}
              onChange={(e) => setNewMedication(e.target.value)}
              placeholder="e.g., Metformin 500mg, Lisinopril 10mg"
              className="bg-white border-input"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  addItem('medications', newMedication);
                  setNewMedication('');
                }
              }}
            />
            <Button
              type="button"
              onClick={() => {
                addItem('medications', newMedication);
                setNewMedication('');
              }}
              className="bg-brand-teal/10 text-brand-teal hover:bg-brand-teal/20 border border-brand-teal/30"
            >
              Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formState.medical_history.medications.map((med, i) => (
              <Badge key={i} variant="outline" className="bg-muted text-foreground border-border">
                {med}
                <button onClick={() => removeItem('medications', i)} className="ml-2 text-destructive">×</button>
              </Badge>
            ))}
          </div>
        </div>}

        {/* Allergies */}
        {showAllergies && <div className="space-y-3">
          <AllergyMultiSelect
            dark
            label="Known Allergies"
            placeholder="Search and select allergies..."
            value={formState.medical_history.allergies}
            onChange={(next) => updateFormState({
              medical_history: {
                ...formState.medical_history,
                allergies: next,
              },
            })}
          />
        </div>}

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={() => {
              const prev = targetedNav.prevStep(2) || 1;
              updateFormState({ currentStep: prev });
              navigate(`${basePath}/${token}/form?step=${prev}`);
            }}
            className="bg-[#1E2028] border-[#3B4252] text-white hover:bg-[#2A2F3A] font-medium"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button
            onClick={() => {
              const next = targetedNav.nextStep(2) || 3;
              updateFormState({ currentStep: next });
              navigate(`${basePath}/${token}/form?step=${next}`);
            }}
            className="bg-gradient-to-r from-brand-teal to-brand-copper hover:from-brand-teal/90 hover:to-brand-copper/90 text-white font-medium"
          >
            {(() => {
              const next = targetedNav.nextStep(2);
              if (next === 3) return 'Next: Questionnaires';
              if (next === 4) return 'Next: Lifestyle';
              if (next === 5) return 'Next: Address';
              if (next === 6) return 'Next: Review';
              return 'Next';
            })()}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Step 3: Questionnaires
const Step3Questionnaires: React.FC = () => {
  const { formState, updateFormState, targetedNav } = useIntakeForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useParams<{ token: string }>();

  const basePath = useMemo(() => {
    const path = location.pathname;
    const formIndex = path.indexOf('/form');
    if (formIndex > 0) {
      const beforeForm = path.substring(0, formIndex);
      const tokenIndex = beforeForm.lastIndexOf('/' + token);
      return tokenIndex > 0 ? beforeForm.substring(0, tokenIndex) : '/patient/intake';
    }
    return '/patient/intake';
  }, [location.pathname, token]);

  const isMale = formState.basic_info.biological_sex === 'male';
  const mf = formState.missing_fields;
  const isTargeted = targetedNav.isTargeted;

  // In targeted mode, only show questionnaires that are in missing_fields
  const showADAM = isMale && (!isTargeted || mf.some(f => ['adam', 'symptoms_assessment', 'symptoms', 'questionnaire'].includes(f)));
  const showStopBang = !isTargeted || mf.some(f => ['stop_bang', 'symptoms_assessment', 'symptoms', 'questionnaire'].includes(f));

  return (
    <div className="space-y-6">
      <Card className="bg-white border shadow-sm">
        <CardHeader>
          <CardTitle className="text-foreground font-montserrat">Clinical Questionnaires</CardTitle>
          <CardDescription className="text-muted-foreground">
            {isTargeted
              ? 'Your provider needs the following assessments completed'
              : 'These validated questionnaires help your provider understand your symptoms'}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* ADAM Questionnaire for males (filtered in targeted mode) */}
      {showADAM && (
        <QuestionnaireADAM
          responses={formState.questionnaires.adam}
          onChange={(responses) => updateFormState({
            questionnaires: { ...formState.questionnaires, adam: responses }
          })}
        />
      )}

      {/* STOP-Bang (filtered in targeted mode) */}
      {showStopBang && (
        <QuestionnaireStopBang
          responses={formState.questionnaires.stopBang}
          onChange={(responses) => updateFormState({
            questionnaires: { ...formState.questionnaires, stopBang: responses }
          })}
          biologicalSex={formState.basic_info.biological_sex}
          dateOfBirth={formState.basic_info.date_of_birth}
        />
      )}

      {/* Navigation */}
      <Card className="bg-white border shadow-sm">
        <CardContent className="pt-6">
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => {
                const prev = targetedNav.prevStep(3) || 2;
                updateFormState({ currentStep: prev });
                navigate(`${basePath}/${token}/form?step=${prev}`);
              }}
              className="bg-[#1E2028] border-[#3B4252] text-white hover:bg-[#2A2F3A] font-medium"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button
              onClick={() => {
                const next = targetedNav.nextStep(3) || 4;
                updateFormState({ currentStep: next });
                navigate(`${basePath}/${token}/form?step=${next}`);
              }}
              className="bg-gradient-to-r from-brand-teal to-brand-copper hover:from-brand-teal/90 hover:to-brand-copper/90 text-white font-medium"
            >
              {targetedNav.nextStep(3) === 6 ? 'Next: Review' : targetedNav.nextStep(3) === 5 ? 'Next: Address' : 'Next: Lifestyle'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Step 4: Lifestyle
const Step4Lifestyle: React.FC = () => {
  const { formState, updateFormState, targetedNav } = useIntakeForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useParams<{ token: string }>();
  const [goals, setGoals] = useState('');

  const basePath = useMemo(() => {
    const path = location.pathname;
    const formIndex = path.indexOf('/form');
    if (formIndex > 0) {
      const beforeForm = path.substring(0, formIndex);
      const tokenIndex = beforeForm.lastIndexOf('/' + token);
      return tokenIndex > 0 ? beforeForm.substring(0, tokenIndex) : '/patient/intake';
    }
    return '/patient/intake';
  }, [location.pathname, token]);

  const handleChange = (field: string, value: string | number | string[]) => {
    updateFormState({
      lifestyle: { ...formState.lifestyle, [field]: value }
    });
  };

  return (
    <Card className="bg-white border shadow-sm">
      <CardHeader>
        <CardTitle className="text-foreground font-montserrat">Lifestyle & Goals</CardTitle>
        <CardDescription className="text-muted-foreground">
          Help us understand your lifestyle to provide personalized recommendations
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Height & Weight */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="lifestyle-height" className="text-foreground required">
              Height (inches)
            </Label>
            <Input
              id="lifestyle-height"
              type="number"
              inputMode="decimal"
              min={20}
              max={96}
              step="0.1"
              placeholder="e.g. 70"
              value={formState.lifestyle.height || ''}
              onChange={(e) => handleChange('height', e.target.value)}
              className="bg-white border-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lifestyle-weight" className="text-foreground required">
              Weight (lbs)
            </Label>
            <Input
              id="lifestyle-weight"
              type="number"
              inputMode="decimal"
              min={20}
              max={1000}
              step="0.1"
              placeholder="e.g. 165"
              value={formState.lifestyle.weight || ''}
              onChange={(e) => handleChange('weight', e.target.value)}
              className="bg-white border-input"
            />
          </div>
        </div>

        {/* Exercise */}
        <div className="space-y-2">
          <Label className="text-foreground">How often do you exercise?</Label>
          <select
            value={formState.lifestyle.exercise_frequency}
            onChange={(e) => handleChange('exercise_frequency', e.target.value)}
            className="w-full bg-white border border-input text-foreground rounded-md p-2"
          >
            <option value="">Select...</option>
            <option value="never">Never</option>
            <option value="rarely">Rarely (1-2 times/month)</option>
            <option value="sometimes">Sometimes (1-2 times/week)</option>
            <option value="regularly">Regularly (3-4 times/week)</option>
            <option value="daily">Daily</option>
          </select>
        </div>

        {/* Sleep */}
        <div className="space-y-2">
          <Label className="text-foreground">Average hours of sleep per night</Label>
          <Input
            type="number"
            value={formState.lifestyle.sleep_hours || ''}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') {
                handleChange('sleep_hours', 0);
              } else {
                const num = parseInt(val);
                if (!isNaN(num) && num >= 0 && num <= 24) {
                  handleChange('sleep_hours', num);
                }
              }
            }}
            className="bg-white border-input"
          />
        </div>

        {/* Stress Level */}
        <div className="space-y-2">
          <Label className="text-foreground">Stress Level (1-10): {formState.lifestyle.stress_level}</Label>
          <input
            type="range"
            min="1"
            max="10"
            value={formState.lifestyle.stress_level}
            onChange={(e) => handleChange('stress_level', parseInt(e.target.value))}
            className="w-full accent-brand-teal"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Low stress</span>
            <span>High stress</span>
          </div>
        </div>

        {/* Goals */}
        <div className="space-y-2">
          <Label className="text-foreground">What are your health goals?</Label>
          <Textarea
            value={goals}
            onChange={(e) => {
              setGoals(e.target.value);
              handleChange('goals', e.target.value.split('\n').filter(g => g.trim()));
            }}
            placeholder="e.g., Improve energy levels, Better sleep, Weight management..."
            className="bg-white border-input min-h-[100px]"
          />
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={() => {
              const prev = targetedNav.prevStep(4) || 3;
              updateFormState({ currentStep: prev });
              navigate(`${basePath}/${token}/form?step=${prev}`);
            }}
            className="bg-[#1E2028] border-[#3B4252] text-white hover:bg-[#2A2F3A] font-medium"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button
            onClick={() => {
              const next = targetedNav.nextStep(4) || 5;
              updateFormState({ currentStep: next });
              navigate(`${basePath}/${token}/form?step=${next}`);
            }}
            className="bg-gradient-to-r from-brand-teal to-brand-copper hover:from-brand-teal/90 hover:to-brand-copper/90 text-white font-medium"
          >
            {targetedNav.nextStep(4) === 6 ? 'Next: Review' : 'Next: Address'}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Step 5: Shipping & Billing Address
const Step5Address: React.FC = () => {
  const { formState, updateFormState, targetedNav } = useIntakeForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useParams<{ token: string }>();

  const basePath = useMemo(() => {
    const path = location.pathname;
    const formIndex = path.indexOf('/form');
    if (formIndex > 0) {
      const beforeForm = path.substring(0, formIndex);
      const tokenIndex = beforeForm.lastIndexOf('/' + token);
      return tokenIndex > 0 ? beforeForm.substring(0, tokenIndex) : '/patient/intake';
    }
    return '/patient/intake';
  }, [location.pathname, token]);

  const handleShippingChange = (field: string, value: string) => {
    const fieldMap: Record<string, string> = {
      addressLine1: 'shippingAddressLine1',
      addressLine2: 'shippingAddressLine2',
      city: 'shippingCity',
      state: 'shippingState',
      postalCode: 'shippingPostalCode',
    };
    updateFormState(prev => ({
      shipping_billing: { ...prev.shipping_billing, [fieldMap[field]]: value }
    }));
  };

  const handleBillingChange = (field: string, value: string) => {
    const fieldMap: Record<string, string> = {
      addressLine1: 'billingAddressLine1',
      addressLine2: 'billingAddressLine2',
      city: 'billingCity',
      state: 'billingState',
      postalCode: 'billingPostalCode',
    };
    updateFormState(prev => ({
      shipping_billing: { ...prev.shipping_billing, [fieldMap[field]]: value }
    }));
  };

  const handleBillingSameToggle = (value: boolean) => {
    updateFormState(prev => ({
      shipping_billing: { ...prev.shipping_billing, billingSameAsShipping: value }
    }));
  };

  const canProceed = () => {
    const sb = formState.shipping_billing;
    const billingOk = Boolean(
      sb.billingAddressLine1?.trim() &&
      sb.billingCity?.trim() &&
      sb.billingState?.trim() &&
      sb.billingPostalCode?.trim() &&
      /^\d{5}(-\d{4})?$/.test(sb.billingPostalCode.trim())
    );
    if (!billingOk) return false;
    if (!sb.billingSameAsShipping) {
      return Boolean(
        sb.shippingAddressLine1?.trim() &&
        sb.shippingCity?.trim() &&
        sb.shippingState?.trim() &&
        sb.shippingPostalCode?.trim() &&
        /^\d{5}(-\d{4})?$/.test(sb.shippingPostalCode.trim())
      );
    }
    return true;
  };

  return (
    <Card className="bg-white/5 border-white/10">
      <CardContent className="pt-6 space-y-6">
        <ShippingBillingAddressStep
          shippingAddress={{
            addressLine1: formState.shipping_billing.shippingAddressLine1,
            addressLine2: formState.shipping_billing.shippingAddressLine2,
            city: formState.shipping_billing.shippingCity,
            state: formState.shipping_billing.shippingState,
            postalCode: formState.shipping_billing.shippingPostalCode,
          }}
          billingAddress={{
            addressLine1: formState.shipping_billing.billingAddressLine1,
            addressLine2: formState.shipping_billing.billingAddressLine2,
            city: formState.shipping_billing.billingCity,
            state: formState.shipping_billing.billingState,
            postalCode: formState.shipping_billing.billingPostalCode,
          }}
          billingSameAsShipping={formState.shipping_billing.billingSameAsShipping}
          onShippingChange={handleShippingChange}
          onBillingChange={handleBillingChange}
          onBillingSameToggle={handleBillingSameToggle}
          variant="dark"
        />

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={() => {
              const prev = targetedNav.prevStep(5) || 4;
              updateFormState({ currentStep: prev });
              navigate(`${basePath}/${token}/form?step=${prev}`);
            }}
            className="bg-[#1E2028] border-[#3B4252] text-white hover:bg-[#2A2F3A] font-medium"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button
            onClick={() => {
              if (!canProceed()) return;
              const next = targetedNav.nextStep(5) || 6;
              updateFormState({ currentStep: next });
              navigate(`${basePath}/${token}/form?step=${next}`);
            }}
            disabled={!canProceed()}
            className="bg-gradient-to-r from-brand-teal to-brand-copper hover:from-brand-teal/90 hover:to-brand-copper/90 text-white font-medium disabled:opacity-50"
          >
            Review & Submit
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Step 6: Review & Submit
const Step6Review: React.FC = () => {
  const { formState, submitIntake, targetedNav, updateFormState } = useIntakeForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useParams<{ token: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookedAppointment, setBookedAppointment] = useState<BookedAppointment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCancelled, setIsCancelled] = useState(false);
  const [cancelledMessage, setCancelledMessage] = useState<string | null>(null);

  const showPayment = formState.isGuided
    && formState.paymentStatus !== 'hold_placed'
    && formState.paymentStatus !== 'captured';

  // Check if already submitted on mount
  useEffect(() => {
    const submittedStatus = sessionStorage.getItem(`patient-intake-submitted-${token}`);
    if (submittedStatus === 'true') {
      setSubmitted(true);
    }
  }, [token]);

  const basePath = useMemo(() => {
    const path = location.pathname;
    const formIndex = path.indexOf('/form');
    if (formIndex > 0) {
      const beforeForm = path.substring(0, formIndex);
      const tokenIndex = beforeForm.lastIndexOf('/' + token);
      return tokenIndex > 0 ? beforeForm.substring(0, tokenIndex) : '/patient/intake';
    }
    return '/patient/intake';
  }, [location.pathname, token]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await submitIntake();

      if (result.success) {
        sessionStorage.setItem(`patient-intake-submitted-${token}`, 'true');
        sessionStorage.removeItem('novamed_intake_form');
        if (result.appointment) setBookedAppointment(result.appointment);
        setSubmitted(true);
        updateFormState({ currentStep: 6 });
        window.dispatchEvent(
          new CustomEvent('patient-intake-submitted', {
            detail: { token },
          })
        );
      } else if (result.isCancelled) {
        setIsCancelled(true);
        setCancelledMessage(result.error || 'This intake link is no longer valid.');
        sessionStorage.removeItem('novamed_intake_form');
      } else {
        setError(result.error || 'Failed to submit intake. Please try again.');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show cancelled state
  if (isCancelled) {
    return (
      <Card className="bg-white/5 border-white/10">
        <CardContent className="pt-12 pb-12 text-center">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="h-10 w-10 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white font-montserrat mb-4">Intake No Longer Available</h2>
          <p className="text-white/60 mb-6 max-w-md mx-auto">
            {cancelledMessage || 'This intake link has been cancelled by your healthcare provider.'}
          </p>
          <p className="text-sm text-white/40">
            Please contact your healthcare provider to request a new intake link if needed.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (submitted) {
    const providerLabel = formState.provider_name ? `Dr. ${formState.provider_name}` : 'your provider';
    const formattedDateTime = bookedAppointment
      ? `${new Date(bookedAppointment.start_time).toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })} at ${new Date(bookedAppointment.start_time).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })}`
      : null;
    return (
      <Card className="bg-white/5 border-white/10">
        <CardContent className="pt-12 pb-12 text-center">
          <div className="w-20 h-20 bg-[#4A8A9A]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-[#4A8A9A]" />
          </div>
          <h2 className="text-2xl font-bold text-white font-montserrat mb-4">Intake Complete!</h2>
          <p className="text-white/80 mb-2 max-w-md mx-auto">
            Thank you for completing your health questionnaire.
          </p>
          {formattedDateTime ? (
            <p className="text-white/70 mb-6 max-w-md mx-auto">
              <span className="font-semibold text-white">Note:</span> Please join your video
              call on <span className="font-semibold text-white">{formattedDateTime}</span>.
              You'll receive a confirmation email with your video visit link.
            </p>
          ) : (
            <p className="text-white/60 mb-6 max-w-md mx-auto">
              Your provider will review your information and prepare personalized recommendations.
            </p>
          )}
          {bookedAppointment ? (
            <div className="bg-[#4A8A9A]/10 border border-[#4A8A9A]/30 rounded-lg p-6 max-w-md mx-auto text-left">
              <h3 className="font-semibold text-[#4A8A9A] mb-2">Your Appointment</h3>
              <p className="text-sm text-white/90 mb-4">{formattedDateTime}</p>
              <CalendarButtons
                appointment={{
                  start_time: bookedAppointment.start_time,
                  end_time: bookedAppointment.end_time,
                  title: `Telehealth Visit with ${providerLabel}`,
                  description: formState.clinic_name || undefined,
                  location:
                    bookedAppointment.telehealthLink || formState.clinic_name || undefined,
                }}
                practitionerName={providerLabel}
                clinicName={formState.clinic_name || undefined}
                telehealthLink={bookedAppointment.telehealthLink ?? undefined}
              />
            </div>
          ) : (
            <div className="bg-[#4A8A9A]/10 border border-[#4A8A9A]/20 rounded-lg p-4 max-w-md mx-auto">
              <p className="text-sm text-[#4A8A9A]">
                <strong>What's next?</strong> Your provider will contact you to discuss your personalized treatment recommendations.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/5 border-white/10">
      <CardHeader>
        <CardTitle className="text-white font-montserrat">Review Your Information</CardTitle>
        <CardDescription className="text-white/60">
          Please review your information before submitting
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary sections */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-[#4A8A9A]/10 p-4 rounded-lg border border-[#4A8A9A]/20">
            <h3 className="font-semibold text-[#4A8A9A] mb-2">Basic Information</h3>
            <p className="text-white">{formState.basic_info.first_name} {formState.basic_info.last_name}</p>
            <p className="text-white/60 text-sm">{formState.basic_info.email}</p>
            <p className="text-white/60 text-sm">{formState.basic_info.biological_sex}, DOB: {formState.basic_info.date_of_birth ? (() => {
              const parts = formState.basic_info.date_of_birth.split('-');
              if (parts.length === 3 && parts[0].length === 4) {
                return `${parts[1]}/${parts[2]}/${parts[0]}`;
              }
              return formState.basic_info.date_of_birth;
            })() : 'Not provided'}</p>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h3 className="font-semibold text-[#4A8A9A] mb-2">Medical History</h3>
            <p className="text-white/60 text-sm">
              {formState.medical_history.conditions.length} conditions, {formState.medical_history.medications.length} medications
            </p>
          </div>
        </div>

        {/* Billing & Shipping Address Summary */}
        <div className="bg-white/5 p-4 rounded-lg border border-white/10">
          <h3 className="font-semibold text-[#4A8A9A] mb-2">Billing & Shipping Address</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs uppercase text-white/40 mb-1">Billing</p>
              <p className="text-white/80 text-sm">
                {formState.shipping_billing.billingAddressLine1 || 'Not provided'}
                {formState.shipping_billing.billingAddressLine2 && `, ${formState.shipping_billing.billingAddressLine2}`}
              </p>
              {formState.shipping_billing.billingCity && (
                <p className="text-white/60 text-sm">
                  {formState.shipping_billing.billingCity}, {formState.shipping_billing.billingState} {formState.shipping_billing.billingPostalCode}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs uppercase text-white/40 mb-1">Shipping</p>
              {formState.shipping_billing.billingSameAsShipping ? (
                <p className="text-white/60 text-sm italic">Same as billing</p>
              ) : (
                <>
                  <p className="text-white/80 text-sm">
                    {formState.shipping_billing.shippingAddressLine1 || 'Not provided'}
                    {formState.shipping_billing.shippingAddressLine2 && `, ${formState.shipping_billing.shippingAddressLine2}`}
                  </p>
                  {formState.shipping_billing.shippingCity && (
                    <p className="text-white/60 text-sm">
                      {formState.shipping_billing.shippingCity}, {formState.shipping_billing.shippingState} {formState.shipping_billing.shippingPostalCode}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Questionnaire Scores */}
        <div className="bg-white/5 p-4 rounded-lg border border-white/10">
          <h3 className="font-semibold text-white mb-2">Questionnaire Scores</h3>
          <div className="flex gap-4">
            {formState.basic_info.biological_sex === 'male' && (
              <Badge className="bg-[#4A8A9A]/10 text-[#4A8A9A] border border-[#4A8A9A]/20">
                ADAM: {Object.values(formState.questionnaires.adam).filter(v => v).length}/10
              </Badge>
            )}
            <Badge className="bg-[#4A8A9A]/10 text-[#4A8A9A] border border-[#4A8A9A]/20">
              STOP-Bang: {Object.values(formState.questionnaires.stopBang).filter(v => v).length}/8
            </Badge>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={() => {
              const prev = targetedNav.prevStep(6) || 5;
              navigate(`${basePath}/${token}/form?step=${prev}`);
            }}
            className="bg-[#1E2028] border-[#3B4252] text-white hover:bg-[#2A2F3A] font-medium"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          {showPayment ? (
            <Button
              onClick={() => {
                const next = targetedNav.nextStep(6) || 7;
                updateFormState({ currentStep: next });
                navigate(`${basePath}/${token}/form?step=${next}`);
              }}
              className="bg-[#4A8A9A] hover:bg-[#4A8A9A]/90 text-white font-medium"
            >
              {targetedNav.nextStep(6) === 8 ? 'Continue to Booking' : 'Continue to Payment'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-[#4A8A9A] hover:bg-[#4A8A9A]/90 text-white font-medium"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Submit Intake
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Auto-save status indicator component
const AutoSaveIndicator: React.FC<{ 
  isSaving: boolean; 
  lastSavedAt: string | null;
  saveError: string | null;
}> = ({ isSaving, lastSavedAt, saveError }) => {
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  if (saveError) {
    return (
      <div className="flex items-center justify-center gap-2 text-xs text-amber-400">
        <AlertCircle className="h-3 w-3" />
        <span>Unable to save - will retry</span>
      </div>
    );
  }

  if (isSaving) {
    return (
      <div className="flex items-center justify-center gap-2 text-xs text-white/60">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Saving...</span>
      </div>
    );
  }

  if (lastSavedAt) {
    return (
      <div className="flex items-center justify-center gap-2 text-xs text-white/60">
        <Save className="h-3 w-3 text-[#4A8A9A]" />
        <span>Saved at {formatTime(lastSavedAt)}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-2 text-xs text-white/60">
      <Save className="h-3 w-3" />
      <span>Progress auto-saves every 30 seconds</span>
    </div>
  );
};

// Step 7: Payment (guided intakes only)
const Step7Payment: React.FC = () => {
  const { formState, submitIntake, targetedNav, updateFormState } = useIntakeForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useParams<{ token: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookedAppointment, setBookedAppointment] = useState<BookedAppointment | null>(null);
  const [error, setError] = useState<string | null>(null);

  const basePath = useMemo(() => {
    const path = location.pathname;
    const formIndex = path.indexOf('/form');
    if (formIndex > 0) {
      const beforeForm = path.substring(0, formIndex);
      const tokenIndex = beforeForm.lastIndexOf('/' + token);
      return tokenIndex > 0 ? beforeForm.substring(0, tokenIndex) : '/patient/intake';
    }
    return '/patient/intake';
  }, [location.pathname, token]);

  const therapyLabel = useMemo<'HRT' | 'TRT' | 'Peptide' | 'GLP-1' | 'Wellness'>(() => {
    const cat = formState.therapyCategory?.toLowerCase();
    if (cat === 'trt') return 'TRT';
    if (cat === 'hrt') return 'HRT';
    if (cat === 'glp1') return 'GLP-1';
    if (cat === 'peptide') return 'Peptide';
    return 'Wellness';
  }, [formState.therapyCategory]);

  const finalizeSubmit = async (payment?: { paymentMethodId: string; setupIntentId: string }) => {
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await submitIntake(payment);
      if (result.success) {
        sessionStorage.setItem(`patient-intake-submitted-${token}`, 'true');
        sessionStorage.removeItem('novamed_intake_form');
        if (result.appointment) setBookedAppointment(result.appointment);
        setSubmitted(true);
        window.dispatchEvent(
          new CustomEvent('patient-intake-submitted', { detail: { token } })
        );
      } else {
        setError(result.error || 'Failed to submit intake.');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentConfirmed = (ids: { paymentMethodId: string; setupIntentId: string }) => {
    finalizeSubmit(ids);
  };

  const handleSkip = () => {
    finalizeSubmit();
  };

  const handleBack = () => {
    const prev = targetedNav.prevStep(7) || 6;
    updateFormState({ currentStep: prev });
    navigate(`${basePath}/${token}/form?step=${prev}`);
  };

  if (submitted) {
    const providerLabel = formState.provider_name ? `Dr. ${formState.provider_name}` : 'your provider';
    const formattedDateTime = bookedAppointment
      ? `${new Date(bookedAppointment.start_time).toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })} at ${new Date(bookedAppointment.start_time).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })}`
      : null;
    return (
      <Card className="bg-white/5 border-white/10">
        <CardContent className="pt-12 pb-12 text-center">
          <div className="w-20 h-20 bg-[#4A8A9A]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-[#4A8A9A]" />
          </div>
          <h2 className="text-2xl font-bold text-white font-montserrat mb-4">Intake Complete!</h2>
          <p className="text-white/80 mb-2 max-w-md mx-auto">
            Thank you for completing your health questionnaire.
          </p>
          {formattedDateTime ? (
            <p className="text-white/70 mb-6 max-w-md mx-auto">
              <span className="font-semibold text-white">Note:</span> Please join your video
              call on <span className="font-semibold text-white">{formattedDateTime}</span>.
              You'll receive a confirmation email with your video visit link.
            </p>
          ) : (
            <p className="text-white/60 mb-6 max-w-md mx-auto">
              Your provider will review your information and follow up shortly.
            </p>
          )}
          {bookedAppointment && (
            <div className="bg-[#4A8A9A]/10 border border-[#4A8A9A]/30 rounded-lg p-6 max-w-md mx-auto text-left">
              <h3 className="font-semibold text-[#4A8A9A] mb-2">Your Appointment</h3>
              <p className="text-sm text-white/90 mb-4">{formattedDateTime}</p>
              <CalendarButtons
                appointment={{
                  start_time: bookedAppointment.start_time,
                  end_time: bookedAppointment.end_time,
                  title: `Telehealth Visit with ${providerLabel}`,
                  description: formState.clinic_name || undefined,
                  location:
                    bookedAppointment.telehealthLink || formState.clinic_name || undefined,
                }}
                practitionerName={providerLabel}
                clinicName={formState.clinic_name || undefined}
                telehealthLink={bookedAppointment.telehealthLink ?? undefined}
              />
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  if (!token) return null;

  return (
    <>
      <PaymentStep
        token={token}
        isGuided={formState.isGuided}
        paymentStatus={formState.paymentStatus as GuidedPaymentStatus | null}
        therapyLabel={therapyLabel}
        isSubmitting={isSubmitting}
        onPaymentConfirmed={handlePaymentConfirmed}
        onSkip={handleSkip}
        onBack={handleBack}
      />
      {error && (
        <div className="mt-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm text-center">
          {error}
        </div>
      )}
    </>
  );
};

// Step 8: Book Appointment (Nimbus-fulfilled guided intakes only)
const Step8Booking: React.FC = () => {
  const { formState, updateFormState, targetedNav } = useIntakeForm();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const basePath = useMemo(() => {
    const path = location.pathname;
    const formIndex = path.indexOf('/form');
    if (formIndex > 0) {
      const beforeForm = path.substring(0, formIndex);
      const tokenIndex = beforeForm.lastIndexOf('/' + token);
      return tokenIndex > 0 ? beforeForm.substring(0, tokenIndex) : '/patient/intake';
    }
    return '/patient/intake';
  }, [location.pathname, token]);

  if (!token) return null;

  return (
    <IntakeBookingStep
      token={token}
      initialSlot={formState.selectedSlot}
      onSlotSelected={(slot) => {
        const next = targetedNav.nextStep(8) || 7;
        updateFormState({ selectedSlot: slot, currentStep: next });
        navigate(`${basePath}/${token}/form?step=${next}`);
      }}
      onBack={() => {
        const prev = targetedNav.prevStep(8) || 6;
        updateFormState({ currentStep: prev });
        navigate(`${basePath}/${token}/form?step=${prev}`);
      }}
    />
  );
};

// Main Form Component
const IntakeForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { token } = useParams<{ token: string }>();
  const { formState, updateFormState, validateToken, targetedNav } = useIntakeForm();
  const step = parseInt(searchParams.get('step') || '1');
  const [isValidating, setIsValidating] = useState(true);
  const [isCancelled, setIsCancelled] = useState(false);
  const [cancelledMessage, setCancelledMessage] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Validate token on mount to check if cancelled
  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setIsValidating(false);
        return;
      }

      const result = await validateToken(token);
      if (!result.success) {
        setIsCancelled(true);
        setCancelledMessage(result.error || 'This intake link is no longer valid.');
        // Clear any saved form data
        sessionStorage.removeItem('novamed_intake_form');
      }
      setIsValidating(false);
    };

    checkToken();
  }, [token, validateToken]);

  useEffect(() => {
    if (!token) {
      setIsSubmitted(false);
      return;
    }
    const submittedStatus = sessionStorage.getItem(`patient-intake-submitted-${token}`);
    setIsSubmitted(submittedStatus === 'true');
  }, [token]);

  useEffect(() => {
    const onSubmitted = (event: Event) => {
      const customEvent = event as CustomEvent<{ token?: string }>;
      if (customEvent.detail?.token === token) {
        setIsSubmitted(true);
      }
    };

    window.addEventListener('patient-intake-submitted', onSubmitted as EventListener);
    return () => {
      window.removeEventListener('patient-intake-submitted', onSubmitted as EventListener);
    };
  }, [token]);

  useEffect(() => {
    if (step !== formState.currentStep) {
      updateFormState({ currentStep: step });
    }
  }, [step, formState.currentStep, updateFormState]);

  // In targeted mode, redirect to first active step if current step is not active
  useEffect(() => {
    if (!isValidating && targetedNav.isTargeted && !targetedNav.activeSteps.includes(formState.currentStep)) {
      const firstActive = targetedNav.activeSteps[0] || 1;
      updateFormState({ currentStep: firstActive });
    }
  }, [isValidating, targetedNav.isTargeted, targetedNav.activeSteps, formState.currentStep, updateFormState]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [formState.currentStep]);

  // Show loading while validating
  if (isValidating) {
    return (
      <div className="min-h-screen bg-[#0A0F1A] flex items-center justify-center intake-dark-form">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-[#4A8A9A]" />
          <p className="text-white/60">Loading your intake form...</p>
        </div>
      </div>
    );
  }

  // Show cancelled message
  if (isCancelled) {
    return (
      <div className="min-h-screen bg-[#0A0F1A] flex items-center justify-center p-4 intake-dark-form">
        <Card className="max-w-md w-full bg-white/5 border-white/10">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-red-400 mx-auto" />
              <h1 className="text-2xl font-bold text-white font-montserrat">
                {cancelledMessage?.includes('cancelled') ? 'Intake Cancelled' : 'Link No Longer Valid'}
              </h1>
              <p className="text-white/60">
                {cancelledMessage}
              </p>
              <p className="text-sm text-white/40">
                Please contact your healthcare provider if you need to complete a new intake.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderStep = () => {
    switch (formState.currentStep) {
      case 1: return <Step1BasicInfo />;
      case 2: return <Step2MedicalHistory />;
      case 3: return <Step3Questionnaires />;
      case 4: return <Step4Lifestyle />;
      case 5: return <Step5Address />;
      case 6: return <Step6Review />;
      case 7: return <Step7Payment />;
      case 8: return <Step8Booking />;
      default: return <Step1BasicInfo />;
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1A] py-12 px-4 intake-dark-form">
      <div className="container mx-auto max-w-4xl">
        <Card className="mb-4 bg-white/5 border-white/10">
          <CardContent className="py-3">
            <p className="text-xs text-white/60 font-medium text-center">
              {formState.clinic_name || 'Nimbus Providers'}
            </p>
            <p className="text-sm text-[#4A8A9A] font-medium text-center">
              {formState.clinic_name ? 'Partnered with Nimbus Providers' : 'Powered by Nimbus Providers'}
            </p>
          </CardContent>
        </Card>
        {targetedNav.isTargeted && (
          <Card className="mb-4 bg-[#BD6F51]/10 border-[#BD6F51]/30">
            <CardContent className="py-3">
              <div className="flex items-center gap-2 justify-center">
                <Sparkles className="h-4 w-4 text-[#B88CC8]" />
                <p className="text-sm text-white/80 text-center">
                  Your provider requested additional information to complete your assessment. This quick intake covers only what&apos;s needed.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        <IntakeProgress
          currentStep={targetedNav.stepPosition(formState.currentStep)}
          totalSteps={targetedNav.totalActiveSteps}
          steps={targetedNav.activeStepConfigs.map((cfg, i) => ({ ...cfg, number: i + 1 }))}
          isCompleted={isSubmitted}
        />
        {renderStep()}
        <div className="mt-4">
          <AutoSaveIndicator 
            isSaving={formState.isSaving} 
            lastSavedAt={formState.lastSavedAt}
            saveError={formState.saveError}
          />
        </div>
      </div>
    </div>
  );
};

// Main Export with Provider
const PatientIntake: React.FC = () => {
  return (
    <IntakeProvider>
      <IntakeLanding />
    </IntakeProvider>
  );
};

export const PatientIntakeForm: React.FC = () => {
  return (
    <IntakeProvider>
      <IntakeForm />
    </IntakeProvider>
  );
};

export default PatientIntake;
